import React, { useCallback, useMemo, useState } from "react";

import { getApiUrl } from "@/config";
import InteractionService from "@/service/Interaction";
import LocalStorageService from "@/service/LocalStorage";
import { Opinion, OpinionResult } from "@/service/Model";
import TrackingService from "@/service/Tracking";

export type StoredOpinion = {
  answer?: number;
  createdAt?: string;
};

export type OpinionProviderProps = {
  interaction: Opinion;
  interactionResults?: OpinionResult;
  preview: boolean;
  children?: React.ReactNode;
};

export type OpinionContextProps = {
  opinionValue?: StoredOpinion;
  preview: boolean;
  onThumbsAnswer: (interactionId: string, opinion: number) => void;
  onMeterAnswer: (interactionId: string, opinion: number) => void;
  onNpsAnswer: (interactionId: string, opinion: number) => void;
  onNpsFeedback: (opinionId: string, answer: number, message: string) => void;
  onNpsClose: (interactionId: string) => void;
  onRatingAnswer: (interactionId: string, opinion: number) => void;
};

const OpinionContext = React.createContext<OpinionContextProps>({
  opinionValue: undefined,
  preview: false,
  onThumbsAnswer: () => undefined,
  onMeterAnswer: () => undefined,
  onNpsAnswer: () => undefined,
  onNpsFeedback: () => undefined,
  onNpsClose: () => undefined,
  onRatingAnswer: () => undefined,
});

const OpinionProvider: React.FC<OpinionProviderProps> = ({
  interaction,
  interactionResults,
  preview,
  children,
}) => {
  const [opinionValue, setOpinionValue] = useState<StoredOpinion | undefined>(
    preview ? undefined : LocalStorageService.getOpinion(interaction.id)
  );

  const api = useMemo(
    () => new InteractionService(getApiUrl(preview)),
    [preview]
  );

  const onVote = useCallback(
    (interactionId: string, opinion: number, defaultLength: number): void => {
      // The opinion is the position of the array, which is marked with the number "1"
      const opinionArray = Array(defaultLength).fill(0);
      opinionArray[opinion] = 1;

      // This is relevant in case the user wants to change his mind,
      // same behavior as thumbs/meter (will probably never happen for nps)
      const storedOpinion = LocalStorageService.getOpinion(interactionId);
      if (storedOpinion && storedOpinion.answer !== undefined) {
        // Reset old opinion with "-1" (Attention: backend deducts the old user opinion (anonymous))
        opinionArray[storedOpinion.answer] -= 1;
      }

      if (!preview) {
        api.postOpinionResults(interactionId, opinionArray);
        // Notice: Don't wait for api response (even if it fails), UI can render immediately
        const createdAt = new Date().toISOString();
        LocalStorageService.setOpinion(interactionId, {
          answer: opinion,
          createdAt,
        });
        TrackingService.getInstance().trackOnVote();
      }

      setOpinionValue({ answer: opinion });
    },
    [preview, api]
  );

  const onMeterAnswer = useCallback(
    (interactionId: string, opinion: number): void => {
      onVote(interactionId, opinion, 7);
    },
    [onVote]
  );

  const onNpsAnswer = useCallback(
    (interactionId: string, opinion: number): void => {
      onVote(interactionId, opinion, 11);
    },
    [onVote]
  );

  const onNpsClose = useCallback(
    (interactionId: string): void => {
      if (!preview && !LocalStorageService.getOpinion(interactionId)) {
        LocalStorageService.setOpinion(interactionId, {
          createdAt: new Date().toISOString(),
        });
      }
    },
    [preview]
  );

  const onNpsFeedback = useCallback(
    (opinionId: string, answer: number, message: string): void => {
      if (!preview) {
        api.postOpinionFeedback(opinionId, answer, message);
      }
    },
    [preview, api]
  );

  const onRatingAnswer = useCallback(
    (interactionId: string, opinion: number): void => {
      onVote(interactionId, opinion, 5);
    },
    [onVote]
  );

  const onThumbsAnswer = useCallback(
    (interactionId: string, opinion: number): void => {
      onVote(interactionId, opinion, 2);
    },
    [onVote]
  );

  const value = useMemo(
    () => ({
      opinionValue,
      preview,
      onThumbsAnswer,
      onMeterAnswer,
      onNpsAnswer,
      onNpsFeedback,
      onNpsClose,
      onRatingAnswer,
    }),
    [
      opinionValue,
      preview,
      onThumbsAnswer,
      onMeterAnswer,
      onNpsAnswer,
      onNpsFeedback,
      onNpsClose,
      onRatingAnswer,
    ]
  );

  return (
    <OpinionContext.Provider value={value}>{children}</OpinionContext.Provider>
  );
};

const OpinionConsumer = OpinionContext.Consumer;

export { OpinionConsumer, OpinionProvider };
export default OpinionContext;
